import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Textarea } from "@shared/ui/textarea";
import { Button } from "@shared/ui/button";
import { useToast } from "@shared/hooks/use-toast";

const SopModals: React.FC = () => {
  const { toast } = useToast();

  // Modal states
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isAddMaterialModalOpen, setIsAddMaterialModalOpen] = useState(false);
  const [isAddReadingModalOpen, setIsAddReadingModalOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isAddHelpModalOpen, setIsAddHelpModalOpen] = useState(false);

  // Handler functions
  const handleAddTask = (data: any) => {
    toast({
      title: "Task Added",
      description: "New task has been added to the SOP",
    });
    setIsAddTaskModalOpen(false);
  };

  const handleAddMaterial = (data: any) => {
    toast({
      title: "Material Added",
      description: "New material has been added to the SOP",
    });
    setIsAddMaterialModalOpen(false);
  };

  const handleAddReading = (data: any) => {
    toast({
      title: "Reading Added",
      description: "New reading has been added to the SOP",
    });
    setIsAddReadingModalOpen(false);
  };

  const handleAddNote = (data: any) => {
    toast({
      title: "Note Added",
      description: "New note has been added to the SOP",
    });
    setIsAddNoteModalOpen(false);
  };

  const handleAddHelp = (data: any) => {
    toast({
      title: "Help Item Added",
      description: "New help resource has been added to the SOP",
    });
    setIsAddHelpModalOpen(false);
  };

  return (
    <>
      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={isAddTaskModalOpen}
        onClose={() => setIsAddTaskModalOpen(false)}
        onSubmit={handleAddTask}
      />

      {/* Add Material Modal */}
      <AddMaterialModal
        isOpen={isAddMaterialModalOpen}
        onClose={() => setIsAddMaterialModalOpen(false)}
        onSubmit={handleAddMaterial}
      />

      {/* Add Reading Modal */}
      <AddReadingModal
        isOpen={isAddReadingModalOpen}
        onClose={() => setIsAddReadingModalOpen(false)}
        onSubmit={handleAddReading}
      />

      {/* Add Help Modal */}
      <AddHelpModal
        isOpen={isAddHelpModalOpen}
        onClose={() => setIsAddHelpModalOpen(false)}
        onSubmit={handleAddHelp}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        onSubmit={handleAddNote}
      />
    </>
  );
};

// Individual Modal Components
const AddTaskModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ isOpen, onClose, onSubmit }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Task</DialogTitle>
          <DialogDescription>
            Add a new task to this SOP template
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="task-name" className="text-right">
              Task Name
            </Label>
            <Input
              id="task-name"
              className="col-span-3"
              placeholder="Enter task name"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="task-duration" className="text-right">
              Time (Mins)
            </Label>
            <Input
              id="task-duration"
              type="number"
              className="col-span-3"
              placeholder="0"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="task-downtime" className="text-right">
              Downtime (Mins)
            </Label>
            <Input
              id="task-downtime"
              type="number"
              className="col-span-3"
              placeholder="0"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="task-skills" className="text-right">
              Skills
            </Label>
            <Input
              id="task-skills"
              className="col-span-3"
              placeholder="Enter skills (comma separated)"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="task-photo" className="text-right">
              Photo Required
            </Label>
            <div className="flex items-center space-x-2">
              <input type="checkbox" id="task-photo" />
              <Label htmlFor="task-photo">Yes</Label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit({})}>Add Task</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddMaterialModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ isOpen, onClose, onSubmit }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Material</DialogTitle>
          <DialogDescription>
            Add a new material to this SOP template
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="material-name" className="text-right">
              Name
            </Label>
            <Input
              id="material-name"
              className="col-span-3"
              placeholder="Enter material name"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="material-size" className="text-right">
              Size
            </Label>
            <Input
              id="material-size"
              className="col-span-3"
              placeholder="Enter size/dimensions"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="material-quantity" className="text-right">
              Quantity
            </Label>
            <Input
              id="material-quantity"
              type="number"
              className="col-span-3"
              placeholder="0"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="material-cost" className="text-right">
              Cost (per unit)
            </Label>
            <Input
              id="material-cost"
              type="number"
              className="col-span-3"
              placeholder="0"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit({})}>Add Material</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddReadingModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ isOpen, onClose, onSubmit }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Reading</DialogTitle>
          <DialogDescription>
            Add a new reading parameter to this SOP template
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="reading-name" className="text-right">
              Name
            </Label>
            <Input
              id="reading-name"
              className="col-span-3"
              placeholder="Enter reading name"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="reading-unit" className="text-right">
              Unit
            </Label>
            <Input
              id="reading-unit"
              className="col-span-3"
              placeholder="Enter unit (e.g., kWh, psi)"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="reading-min" className="text-right">
              Min Value
            </Label>
            <Input
              id="reading-min"
              type="number"
              className="col-span-3"
              placeholder="0"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="reading-max" className="text-right">
              Max Value
            </Label>
            <Input
              id="reading-max"
              type="number"
              className="col-span-3"
              placeholder="0"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="reading-photo" className="text-right">
              Photo Required
            </Label>
            <div className="flex items-center space-x-2">
              <input type="checkbox" id="reading-photo" />
              <Label htmlFor="reading-photo">Yes</Label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit({})}>Add Reading</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddHelpModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ isOpen, onClose, onSubmit }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Help Resource</DialogTitle>
          <DialogDescription>
            Add a new help resource to this SOP template
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="help-type" className="text-right">
              Type
            </Label>
            <select
              id="help-type"
              className="col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
            >
              <option value="link">Link</option>
              <option value="instruction">Instruction</option>
              <option value="video">Video</option>
              <option value="document">Document</option>
            </select>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="help-details" className="text-right">
              Details
            </Label>
            <Textarea
              id="help-details"
              className="col-span-3"
              placeholder="Enter help details or URL"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit({})}>Add Help Resource</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddNoteModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ isOpen, onClose, onSubmit }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Note</DialogTitle>
          <DialogDescription>
            Add a new note to this SOP template
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="note-content" className="text-right">
              Note
            </Label>
            <Textarea
              id="note-content"
              className="col-span-3"
              placeholder="Enter note content"
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit({})}>Add Note</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SopModals;
